// packages import
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { teal } from "@mui/material/colors";
import axios, { isAxiosError } from 'axios';
import { FC, useContext, useEffect, useState } from "react";
import { useDropzone } from 'react-dropzone';
// component import
import { Alert } from "./Alert";
import { CustomModal } from "./CustomModal";
// others
import { DocumentIcon, DocumentUploadedIcon } from "../../assets/images";
import { AUTH_TOKEN, CONFLICT_EXCEPTION, CSV_FORMAT_INCORRECT, PHONE_FORMAT_NOT_CORRECT, USER_ALREADY_EXIST } from "../../constants";
import { AppContext } from "../../context";
import { ActionType } from "../../context/AppContextReducer";
import { UploadInitialCsvProps } from "../../interfaceTypes";
import { Container, DocumentBox } from "../../theme/styleComponents";

export const UploadInitialCsv: FC<UploadInitialCsvProps> = ({ refetch }) => {
  const { isInitialCsvUpload, dispatch } = useContext(AppContext)
  const [files, setFiles] = useState<File[]>([])
  const [uploaded, setIsUploaded] = useState<boolean>(false)
  const token = localStorage.getItem(AUTH_TOKEN);

  const handleClose = () => dispatch({ type: ActionType.SET_IS_INITIAL_CSV_Upload, isInitialCsvUpload: false })

  const onDrop = async (acceptedFiles: File[]) => {
    setFiles(acceptedFiles.map(file => Object.assign(file, {
      preview: URL.createObjectURL(file)
    })));

    const formData = new FormData();

    if (acceptedFiles.length !== 0) {
      formData.append("title", acceptedFiles[0].name);
      formData.append("file", acceptedFiles[0]);
      setIsUploaded(true)

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}${'/cases/importCasesFromCsv'}`,
          formData,
          {
            headers: {
              authorization: `Bearer ${token}`
            }
          }
        )

        const { data } = response
        const { response: { status } } = data

        if (status === 200) {
          setIsUploaded(false)
          Alert.success('Cases created successfully');
          refetch && refetch()
          setFiles([])
          dispatch({ type: ActionType.SET_IS_INITIAL_CSV_Upload, isInitialCsvUpload: false })
        }
      }

      catch (error) {
        setIsUploaded(false)
        setFiles([])

        if (isAxiosError(error)) {
          const { response: { status, data } = {} } = error;
          const { error: errorText } = data || {}
          const { message } = errorText || {}

          if (status === 400 && !message) {
            const caseNumber = data && errorText && errorText?.match(/(\d|,)+/g).pop();
            Alert.error(`Matter number: ${caseNumber} already exist`);
          }

          if (status === 400 && message) {
            Alert.error(message);
          }

          if (status === 409 && error) {
            const { response } = error || {}
            const { data } = response || {}
            const { message: errorMessage } = data || {}

            if (errorMessage === CONFLICT_EXCEPTION) {
              Alert.error(USER_ALREADY_EXIST);
            } else {
              if (!errorMessage) {
                Alert.error(CSV_FORMAT_INCORRECT);
              } else {
                Alert.error(errorMessage);
              }
            }
          }

          if (status === 500 && error) {
            const { response } = error || {}
            const { data } = response || {}
            const { message: errorMessage } = data || {}

            if (errorMessage === PHONE_FORMAT_NOT_CORRECT) {
              Alert.error(PHONE_FORMAT_NOT_CORRECT);
            }
          }
        }
      }
    }
  }

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, fileRejections } = useDropzone({
    onDrop: onDrop,
    accept: {
      'file/csv': ['.csv'],
    },
  });

  useEffect(() => {
    fileRejections.map(({ errors }) => (
      errors.map((messages) => {
        const { message } = messages || {}

        return Alert.error(message)
      }))
    )
  }, [fileRejections])

  return (
    <CustomModal title='Upload Initial CSV' isOpen={isInitialCsvUpload} handleClose={handleClose}>

      <Box className="container" sx={{ cursor: 'pointer', pointerEvents: uploaded ? 'none' : 'unset' }}>
        <Container {...getRootProps({ isFocused, isDragAccept, isDragReject })}>
          <input {...getInputProps()} type="file" />

          <IconButton className="uploadButton">
            <DocumentIcon />
          </IconButton>

          <Typography variant="body2" color='darkOne' fontWeight="400" margin="8px 0px">
            <Box component='span' color='darkRed' sx={{ textDecoration: 'underline' }}>Click to upload</Box> or drag and drop
          </Typography>

          <Typography variant="subtitle1" color='lightGrayOne'>CSV (max. 3 MB)</Typography>
        </Container>
      </Box>

      <Box mb='20px'>
        {files.map((file, index) => {
          const { name } = file || {}

          return (
            <DocumentBox key={index}>
              <Box display="flex" alignItems="center">
                <IconButton className="MuiIconButton-document">
                  <DocumentIcon />
                </IconButton>

                <Box paddingLeft={2}>
                  <Typography variant="body1" fontSize="16px" letterSpacing="0.17px">{name.replace(/ /g, '-')}</Typography>
                </Box>
              </Box>

              {uploaded
                ?
                <Box display="flex" alignItems="center">
                  <Box marginRight={2} marginTop="5px">
                    <CircularProgress size={35} sx={{ color: teal[100] }} />
                  </Box>
                </Box>
                :
                <Box display="flex" alignItems="center">
                  <IconButton className="MuiIconButton-document">
                    <DocumentUploadedIcon />
                  </IconButton>
                </Box>
              }
            </DocumentBox>
          )
        })}
      </Box>
    </CustomModal>
  )
}